/**
 * When it is acceptable to apply a pending app update by reloading the page.
 *
 * ## The rule
 *
 * **The user is never asked, and never interrupted.** An update lands at a
 * moment the page was being torn down anyway:
 *
 *  1. On a NAVIGATION — the route is changing, so a reload costs the render
 *     that was about to happen regardless. This is the one that reaches a
 *     desktop tab left open all day, which nothing else could.
 *  2. On a RETURN after a real absence — he has picked the phone up and is
 *     demonstrably between things.
 *  3. On a COLD START, which needs no code at all: with no old client left to
 *     control, a waiting worker activates on its own.
 *
 * And one exception, which is the only interruption left in the product: a
 * FORCED update, bumped by hand in `public/release.json` when a fix is one
 * people are actively being hurt by. See `src/lib/release/gate.ts`.
 *
 * ## What this replaced, and why
 *
 * There used to be a bar: *"New version available — Refresh"*. It was the right
 * shape for a shop that ships once a week and the wrong one for us. Every merge
 * to `main` deploys production — 20 to 62 a day over the fortnight to 9
 * September 2026 — so within one poll interval every open client had something
 * to be asked about. One owner wrote it down: *"every day tells me a new
 * version and to refresh."*
 *
 * The answer that was tried first was to ask LESS: a release number that moved
 * once a day, so the bar appeared once a day instead of hourly. That works, and
 * it still asks. Asking is the part that was wrong. Nobody running a business
 * wants to be told their software has been updated; they want it to be updated.
 * Amazon deploys every twelve seconds and has never once put a bar in front of
 * anybody. So the bar is gone, the release cadence that existed to ration it is
 * gone with it, and what is left is the two moments where a reload is free.
 *
 * The arithmetic is here rather than in the component because it is the part
 * worth testing, and because "was the app actually away" is the half that is
 * easy to get subtly wrong.
 */

/**
 * How long the app must have been hidden before coming back counts as
 * REOPENING it rather than glancing away.
 *
 * 🚨 The number has to survive an engineer photographing a boiler. Capacitor
 * reports "backgrounded" for the camera, the photo picker and the share sheet,
 * and those take minutes routinely — `RELOCK_AFTER_MS` in
 * `src/lib/native/app-lock.ts` is 15 minutes for exactly that reason, after a
 * 30-second version put a lock screen in front of every job photo. This is the
 * same question about the same absence, so it gets the same answer.
 *
 * Deliberately NOT shared with that constant by import: they are free to move
 * apart, and a lock threshold changing for security reasons must not silently
 * change when the app reloads itself.
 */
export const REOPENED_AFTER_MS = 15 * 60_000;

/**
 * How long this client must have been running before a navigation may reload
 * it.
 *
 * 🚨 Without a floor here, "apply on navigation" means a full page load on the
 * first click after every deploy — up to sixty a day for somebody working in
 * the app all afternoon. A reload at a navigation boundary is cheap; sixty of
 * them is not, and "the app feels slow" is a worse complaint than the bar ever
 * was, because there would be nothing on screen to explain it.
 *
 * Thirty minutes bounds staleness at roughly half an hour of continuous use
 * while costing at most one extra page load in that time — and only ever at a
 * moment the user was already changing page. A client that is genuinely idle
 * gets it on the next return instead, and one that is closed gets it free.
 */
export const MIN_UPTIME_BEFORE_NAV_APPLY_MS = 30 * 60_000;

/**
 * After this long with an update waiting, ANY navigation will do.
 *
 * 🚨 This is the backstop that makes pinning to a landing page safe. On its
 * own, "apply when he arrives at Schedule" fails by NEVER FIRING: an office
 * manager who lives in Quotes and Invoices all day never goes there, so he
 * would sit on the morning's client until he closed the tab. That failure is
 * silent and looks exactly like everything working.
 *
 * Four hours is long enough that a normal working session takes the gentle
 * path, and short enough that nobody spends a day on stale code. It still only
 * ever fires ON a navigation, so it is never a reload out of nowhere.
 */
export const STALE_ENOUGH_FOR_ANY_PAGE_MS = 4 * 60 * 60_000;

/**
 * How long after a client starts the landing page still counts as "they have
 * just arrived", and a waiting update is taken with no floor at all.
 *
 * The moment is the cheapest reload in the product: nothing is half-typed,
 * nothing is open, and the shell is served from the service worker's cache.
 * Taking it here is a reload that does NOT happen later, mid-job.
 *
 * 🚨 A WINDOW, NOT AN EVENT, AND THE FIRST VERSION OF THIS WAS AN EVENT.
 * It tracked "the first arrival at home" with a boolean, which reads as the
 * same idea and is not: `RootLandingRedirect` sends `/` to the landing page
 * from a `useEffect`, so the arrival happens within MILLISECONDS of the client
 * starting, while a new service worker takes SECONDS to install — `sw.js`
 * precaches every offline page and scrapes their chunks before it reaches
 * `installed`. The arrival was therefore always spent before there was
 * anything to apply, and the exemption would have shipped inert: correct,
 * tested, reviewed, and never once firing (CLAUDE.md #34).
 *
 * Two minutes is long enough to outlast that install on a poor connection and
 * short enough that it cannot be mistaken for a working session.
 */
export const LOGIN_LANDING_GRACE_MS = 2 * 60_000;

/**
 * Which rule took the update. Returned rather than a bare boolean so the
 * analytics event can name it — without that, "this rule never fires" and
 * "this rule fires constantly" are the same observation from outside.
 */
export type ApplyRule = 'forced' | 'landing' | 'home' | 'stale' | 'return';

/**
 * What made us ask. 🚨 LOAD-BEARING, and its absence was a real defect.
 *
 * The login fix added a second caller — a worker finishing its install — and
 * the first cut let that caller satisfy EVERY rule. `toLandingPage` means "is
 * on", not "arrived at", so an office manager sat on `/schedule` for half an
 * hour was reloaded with no navigation at all, job modal open, nothing on
 * screen to explain it. On native it was worse: a WebView page lives for days,
 * so every phone is past the four-hour tier by lunchtime and a twenty-second
 * glance at the camera reloaded the app. That is precisely the silent mid-shift
 * reload removed on 2026-09-01, back with a delay on it.
 *
 * So the worker trigger may only satisfy the rules that are about a MOMENT
 * (the login landing, and a forced update). The rules that are about a
 * BOUNDARY need an actual boundary.
 */
export type ApplyTrigger = 'navigation' | 'worker' | 'return';

export interface ApplyNowFacts {
  /** What made us ask — see `ApplyTrigger`. */
  trigger: ApplyTrigger;
  /** How long this client has been running, in ms. */
  uptimeMs: number;
  /**
   * Is the current route this breakpoint's home — `/overview` on a phone,
   * `/schedule` on a desktop (`src/lib/landing-path.ts`)?
   *
   * 🚨 "Is on", not "arrived at". Whether they ARRIVED is `trigger ===
   * 'navigation'`, and the two must be read together — see `ApplyTrigger`.
   *
   * Arriving at home base is the cheapest possible moment to reload: it is
   * where somebody goes BETWEEN things, it holds no half-finished work, and it
   * is the page the app itself sends `/` to.
   */
  toLandingPage: boolean;
  /** Is anything on screen asking us not to reload? (`reloadsHeld()`) */
  reloadsHeld: boolean;
  /** Is there actually a new worker waiting to take over? */
  updatePending: boolean;

  /**
   * Has somebody bumped the forced-update number past the one this build was
   * compiled with? Skips both uptime floors — that is the whole point of it.
   */
  forced?: boolean;
}

/**
 * Should a pending update be applied right now?
 *
 * 🚨 Called at every moment that could change the answer, NOT only on a
 * navigation — the function was `shouldApplyOnNavigation` and the name was the
 * bug. At a login the arrival at home happens milliseconds after the client
 * starts, while the new worker takes seconds to install, so a rule evaluated
 * only when the route changes is asked exactly once, too early, and never
 * again. It is now asked on a navigation AND whenever a worker becomes
 * available.
 *
 * Three tiers, gentlest first:
 *
 *   1. Sitting on home in the first two minutes — the landing after a login or
 *      a cold start. No uptime floor: this is the start of a session rather
 *      than a moment inside one, so there is nothing to interrupt, and taking
 *      it here is a reload NOT happening later when they might be busy.
 *   2. Arriving at HOME (`/overview` on a phone, `/schedule` on a desktop),
 *      once the client has been up half an hour. Home base is where somebody
 *      goes between jobs, so a reload there costs a render that was happening
 *      anyway and interrupts nothing.
 *   3. ANY page, once the client has been up four hours. The backstop, without
 *      which tier 2 fails by never firing for somebody who lives in Quotes.
 *   4. A FORCED update, immediately, wherever they are.
 *
 * 🚨 Tiers 2 and 3 require an actual NAVIGATION. Tier 1 and a forced update do
 * not, because they are about a moment rather than a boundary. See
 * `ApplyTrigger` for what happened when that distinction was missing.
 *
 * 🚨 A hold outranks all three, forced included. The rails that hold are a live
 * voice conversation and the onboarding chat, and reloading either is
 * indistinguishable from Mucka hanging up on the customer — worse than running
 * yesterday's code for another ten minutes, whatever the fix is. Note that a
 * call SURVIVES navigation by design (TKT-1159, an owner asked us to "stay
 * connected to voice whilst cycling through tabs"), so somebody can walk the
 * whole app mid-conversation and this must refuse the whole way round.
 * `reload-guard.ts` has the incident.
 */
export function updateApplyReason(facts: ApplyNowFacts): Exclude<ApplyRule, 'return'> | null {
  if (!facts.updatePending) return null;
  if (facts.reloadsHeld) return null;

  // A deliberate act, for a fix people are being hurt by. Any trigger.
  if (facts.forced) return 'forced';

  // The login landing. A MOMENT rather than a boundary, so the worker trigger
  // counts: at a login the arrival happens milliseconds after the client
  // starts while a worker takes seconds to install, so waiting for a
  // navigation means waiting for one that already happened.
  if (facts.toLandingPage && facts.uptimeMs <= LOGIN_LANDING_GRACE_MS) return 'landing';

  // Everything below is about a BOUNDARY — the page was being torn down
  // anyway — so it needs a real navigation. Reached from a worker install it
  // would be a reload out of nowhere, which is the whole thing we removed.
  if (facts.trigger !== 'navigation') return null;

  if (facts.uptimeMs >= STALE_ENOUGH_FOR_ANY_PAGE_MS) return 'stale';
  if (facts.toLandingPage && facts.uptimeMs >= MIN_UPTIME_BEFORE_NAV_APPLY_MS) return 'home';
  return null;
}

/** Boolean form, for call sites that do not need to name the rule. */
export function shouldApplyNow(facts: ApplyNowFacts): boolean {
  return updateApplyReason(facts) !== null;
}

export interface ApplyOnReturnFacts {
  /** How long the page was hidden, in ms. */
  hiddenForMs: number;
  /** Is anything on screen asking us not to reload? (`reloadsHeld()`) */
  reloadsHeld: boolean;
  /** Is there actually a new worker waiting to take over? */
  updatePending: boolean;
}

/**
 * Should returning to the foreground apply a pending update?
 *
 * Every "no" here is a reload NOT happening under somebody's hands, so the
 * default is no and each yes has to be earned.
 */
export function shouldApplyOnReturn(facts: ApplyOnReturnFacts): boolean {
  if (!facts.updatePending) return false;
  // A hold outranks the clock. Somebody can be on a voice call with the phone
  // in their pocket for twenty minutes; that is not an absence, it is a call.
  if (facts.reloadsHeld) return false;
  return facts.hiddenForMs >= REOPENED_AFTER_MS;
}

/**
 * The commit a service worker's `BUILD_ID` was stamped from, or null.
 *
 * `scripts/stamp-sw.sh` writes `mucka-pro-<git rev-parse --short HEAD>-<epoch>`
 * in `prebuild`, and `next.config.ts` bakes `NEXT_PUBLIC_BUILD_HASH` from the
 * same `git rev-parse --short HEAD` in the same build. So the two name the same
 * commit exactly when the page and the worker came out of the same build (or
 * a redeploy of the same commit, which serves identical code).
 */
export function commitOfWorkerBuild(buildId: string | null | undefined): string | null {
  const match = /^mucka-pro-([0-9a-f]{4,40})-\d+$/.exec(buildId ?? '');
  return match ? match[1] : null;
}

/**
 * Is this page ALREADY running the build the new worker comes from?
 *
 * 🚨 Then a reload gains nothing and costs whatever the page was doing. On a
 * cold start the document comes from the network (navigations are
 * network-first), so the page is usually current already; the worker that
 * installs in the same seconds is the same build. The landing rule reloaded
 * it anyway — on 11 Oct 2026 two seconds after an engineer tapped Schedule,
 * back to Overview, cancelling the Schedule chunk mid-download
 * (MUCKA-WEB-CV). Only a definite match says yes: an unknown page build
 * (`dev`, unset) or a worker that did not answer means reload as before.
 */
export function alreadyOnWorkerBuild(
  pageBuild: string | null | undefined,
  workerBuildId: string | null | undefined,
): boolean {
  if (!pageBuild || pageBuild === 'dev') return false;
  const workerCommit = commitOfWorkerBuild(workerBuildId);
  return workerCommit !== null && workerCommit === pageBuild;
}
